import { Box, Grid, Flex, Spinner, Center } from '@chakra-ui/react'
import {
  useGetStatsDataQuery,
  useSearchSpoofingReportsCountQuery,
} from '@/generated/graphql'
import { StatsDashboardNumberWidgets } from '@/components/stats/dashboard/shared_stats_widgets'
import { getOrgData, useIsEmployee, useUserID } from '@/hooks/id_token_claims'
import {
  last30DaysRange,
  startDateOrDefault,
  endDateOrDefault,
} from '@/components/stats/dashboard/shared_stats_widgets'
import { SupportedMaterializedViews } from '@/generated/enums'
import { StatType } from '@/generated/enums'
import { ProductType } from '@/generated/enums'
import ListDetail from '@/components/shared/list_detail'
import AlertsSubsetTable from '@/components/shared/alerts_subset_table'
import { ReportFilterType, ReportStatus } from '@/utils/constants'
import { spoofingReportsWhereClause } from '@/hooks/spoof_report_table_filters'
import { ReportType } from '@/utils/constants'
import moment from 'moment'
import { ReportFilter } from '@/hooks/report_table_filters'

export default function HomeComponent() {
  const [userId] = useUserID()
  const isEmployee = useIsEmployee()
  const [orgData] = getOrgData()
  const orgId = orgData?.id
  const orgName = orgData?.name

  // Get all product types and join them into a comma-separated string
  const allProductTypes = Object.values(ProductType).join(',')
  const filterBy = { product: allProductTypes }

  // Stats at top of page
  const {
    data: alertsResolvedTotalData,
    loading: alertsResolvedTotalLoading,
    error: alertsResolvedTotalError,
  } = useGetStatsDataQuery({
    variables: {
      org_id: orgId,
      filter_by: filterBy,
      group_by: undefined,
      start_date: startDateOrDefault(null, last30DaysRange),
      end_date: endDateOrDefault(null, last30DaysRange),
      is_init_run: false,
      graph_name: SupportedMaterializedViews.MV_ALERTS_TIMES_TO_ACTIONED,
      stat_type: StatType.ALERTS_RESOLVED_TOTAL_CREATED,
    },
  })
  const alertsResolvedTotalCount = alertsResolvedTotalData
    ? alertsResolvedTotalData.get_stats_data.data.value.toLocaleString()
    : null

  const {
    data: alertsSurfacedTotalData,
    loading: alertsSurfacedTotalLoading,
    error: alertsSurfacedTotalError,
  } = useGetStatsDataQuery({
    variables: {
      org_id: orgId,
      filter_by: filterBy,
      group_by: undefined,
      start_date: startDateOrDefault(null, last30DaysRange),
      end_date: endDateOrDefault(null, last30DaysRange),
      is_init_run: false,
      graph_name: SupportedMaterializedViews.MV_ALERTS_SURFACED_BY_DAY,
      stat_type: StatType.ALERTS_SURFACED_TOTAL_CREATED,
    },
  })
  const alertsSurfacedTotalCount = alertsSurfacedTotalData
    ? alertsSurfacedTotalData.get_stats_data.data.value.toLocaleString()
    : null

  const {
    data: alertsVerifiedTotalData,
    loading: alertsVerifiedTotalLoading,
    error: alertsVerifiedTotalError,
  } = useGetStatsDataQuery({
    variables: {
      org_id: orgId,
      filter_by: filterBy,
      group_by: undefined,
      start_date: startDateOrDefault(null, last30DaysRange),
      end_date: endDateOrDefault(null, last30DaysRange),
      is_init_run: false,
      graph_name: SupportedMaterializedViews.MV_ALERTS_VERIFIED,
      stat_type: StatType.ALERTS_VERIFIED_TOTAL_CREATED,
    },
  })
  const alertsVerifiedTotalCount = alertsVerifiedTotalData
    ? alertsVerifiedTotalData.get_stats_data.data.value.toLocaleString()
    : null

  const {
    data: medianMitigationTimeData,
    loading: medianMitigationTimeLoading,
    error: medianMitigationTimeError,
  } = useGetStatsDataQuery({
    variables: {
      org_id: orgId,
      filter_by: filterBy,
      group_by: undefined,
      start_date: startDateOrDefault(null, last30DaysRange),
      end_date: endDateOrDefault(null, last30DaysRange),
      is_init_run: false,
      graph_name: SupportedMaterializedViews.MV_ALERTS_TIMES_TO_ACTIONED,
      stat_type: StatType.ALERTS_MEDIAN_MITIGATION_TIME,
    },
  })
  const medianMitigationTime = medianMitigationTimeData
    ? parseFloat(
        medianMitigationTimeData.get_stats_data.data.value.toFixed(1),
      ).toLocaleString() + 'h'
    : null

  const {
    data: medianResolutionTimeData,
    loading: medianResolutionTimeLoading,
    error: medianResolutionTimeError,
  } = useGetStatsDataQuery({
    variables: {
      org_id: orgId,
      filter_by: filterBy,
      group_by: undefined,
      start_date: startDateOrDefault(null, last30DaysRange),
      end_date: endDateOrDefault(null, last30DaysRange),
      is_init_run: false,
      graph_name: SupportedMaterializedViews.MV_ALERTS_TIMES_TO_ACTIONED,
      stat_type: StatType.ALERTS_MEDIAN_RESOLUTION_TIME,
    },
  })
  const medianResolutionTime = medianResolutionTimeData
    ? parseFloat(
        medianResolutionTimeData.get_stats_data.data.value.toFixed(1),
      ).toLocaleString() + 'h'
    : null

  const assigneeReportFilter: ReportFilter = {
    filterType: isEmployee
      ? ReportFilterType.Assignee
      : ReportFilterType.ExternalAssignee,
    value: userId,
  }
  const assignedAlertsSpoofReportsWhere = spoofingReportsWhereClause(
    [assigneeReportFilter],
    orgId,
    [
      ReportStatus.ENUM_NEEDS_CONFIRMATION,
      ReportStatus.NEEDS_CONFIRMATION,
      ReportStatus.ENUM_NEEDS_REVIEW,
    ],
    Object.values(ReportType),
  )
  const { data: numAssignedAlertsData, loading: numAssignedAlertsLoading } =
    useSearchSpoofingReportsCountQuery({
      variables: {
        spoofingReportsWhere: assignedAlertsSpoofReportsWhere,
        skipNormalSearch: false,
        skipFullTextSearch: true,
      },
    })
  const numAssignedAlerts =
    numAssignedAlertsData?.spoofing_reports_aggregate?.aggregate?.count

  // Needs Confirmation ListItem
  const needsConfirmationSpoofReportsWhere = spoofingReportsWhereClause(
    [],
    orgId,
    [ReportStatus.ENUM_NEEDS_CONFIRMATION],
    Object.values(ReportType),
  )
  const {
    data: numAlertsNeedConfirmationData,
    loading: numAlertsNeedConfirmationLoading,
  } = useSearchSpoofingReportsCountQuery({
    variables: {
      spoofingReportsWhere: needsConfirmationSpoofReportsWhere,
      skipNormalSearch: false,
      skipFullTextSearch: true,
    },
  })
  const numAlertsNeedConfirmation =
    numAlertsNeedConfirmationData?.spoofing_reports_aggregate?.aggregate?.count

  const sevenDaysAgo = moment().subtract(7, 'days').startOf('day')
  const now = moment().endOf('day')
  const newAlertsSpoofReportsWhere = spoofingReportsWhereClause(
    [
      {
        filterType: ReportFilterType.DateRange,
        value: `${sevenDaysAgo.toISOString()}#${now.toISOString()}`,
      },
    ],
    orgId,
    [],
    Object.values(ReportType),
  )
  const { data: numNewAlertsCreatedData, loading: numNewAlertsCreatedDataLoading } =
    useSearchSpoofingReportsCountQuery({
      variables: {
        spoofingReportsWhere: newAlertsSpoofReportsWhere,
        skipNormalSearch: false,
        skipFullTextSearch: true,
      },
    })
  const numNewAlertsCreated =
    numNewAlertsCreatedData?.spoofing_reports_aggregate?.aggregate?.count

  // Calculate dashboard items dynamically based on available data
  const listItems = []

  if (numAssignedAlerts && numAssignedAlerts > 0) {
    listItems.push({
      id: `assigned-alerts-${orgId}`,
      label: `${numAssignedAlerts} alert${
        numAssignedAlerts === 1 ? '' : 's'
      } assigned to you`,
      isUrgent: true,
      timestamp: new Date(),
      details: (
        <AlertsSubsetTable
          numAlerts={numAssignedAlerts}
          orgId={orgId}
          spoofReportsWhere={assignedAlertsSpoofReportsWhere}
        />
      ),
    })
  }

  if (numAlertsNeedConfirmation && numAlertsNeedConfirmation > 0) {
    listItems.push({
      id: `needs-confirmation-${orgId}`,
      label: `${numAlertsNeedConfirmation} alert${
        numAlertsNeedConfirmation === 1 ? '' : 's'
      } need confirmation`,
      isUrgent: true,
      timestamp: new Date(),
      details: (
        <AlertsSubsetTable
          numAlerts={numAlertsNeedConfirmation}
          orgId={orgId}
          spoofReportsWhere={needsConfirmationSpoofReportsWhere}
        />
      ),
    })
  }

  if (numNewAlertsCreated && numNewAlertsCreated > 0) {
    listItems.push({
      id: `new-alerts-${orgId}`,
      label: `${numNewAlertsCreated} alert${
        numNewAlertsCreated === 1 ? '' : 's'
      } created in the past 7 days`,
      timestamp: new Date(),
      details: (
        <AlertsSubsetTable
          numAlerts={numNewAlertsCreated}
          orgId={orgId}
          spoofReportsWhere={newAlertsSpoofReportsWhere}
        />
      ),
    })
  }

  return (
    <Flex direction="column" h="100vh" px={8} py={4} w="100%">
      <Box mb={4} w="100%">
        <Grid gap={4} templateColumns="repeat(30, 1fr)" w="100%">
          <StatsDashboardNumberWidgets
            alertsResolvedTotal={{
              stat: alertsResolvedTotalCount,
              error: alertsResolvedTotalError,
              loading: alertsResolvedTotalLoading,
            }}
            alertsSurfacedTotal={{
              stat: alertsSurfacedTotalCount,
              error: alertsSurfacedTotalError,
              loading: alertsSurfacedTotalLoading,
            }}
            alertsVerifiedTotal={{
              stat: alertsVerifiedTotalCount,
              error: alertsVerifiedTotalError,
              loading: alertsVerifiedTotalLoading,
            }}
            datePickerRange={last30DaysRange}
            medianMitigationTime={{
              stat: medianMitigationTime,
              error: medianMitigationTimeError,
              loading: medianMitigationTimeLoading,
            }}
            medianResolutionTime={{
              stat: medianResolutionTime,
              error: medianResolutionTimeError,
              loading: medianResolutionTimeLoading,
            }}
            orgName={orgName}
          />
        </Grid>
      </Box>

      <Box flex="1" overflowY="auto">
        {numAlertsNeedConfirmationLoading ||
        numNewAlertsCreatedDataLoading ||
        numAssignedAlertsLoading ? (
          <Center p={8}>
            <Spinner size="xl" />
          </Center>
        ) : (
          <ListDetail items={listItems} title="Dashboard" />
        )}
      </Box>
    </Flex>
  )
}
